import { useFrame, useThree } from '@react-three/fiber'
import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests } from 'features/auth'
import { PointCloudCache } from 'features/pointClouds/pcCache'
import { PointCloudOctree, Potree } from 'gecko-potree-core'
import { isNil } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { Object3D } from 'three'
import { GeckoPotreePointCloud } from 'types'

const defaultFetch = async (
  url: RequestInfo,
  baseUrl: string,
  axios: AuthedAxiosRequests,
  init?: RequestInit
): Promise<Response> => {
  //This Fetch is used to get the signed url and then the data for the point cloud
  const authUrl = `${baseUrl}/${url}`
  const response = await axios.get(authUrl)
  return fetch(response.data, init)
}

async function fetchWithCache({
  url,
  baseUrl,
  axios,
  cache,
  entryId,
  generation,
  init,
}: {
  url: RequestInfo
  baseUrl: string
  axios: AuthedAxiosRequests
  cache: PointCloudCache
  entryId: string
  generation: number
  init?: RequestInit
}): Promise<Response> {
  if (init?.headers) {
    const headers = new Headers(init?.headers)
    const range = headers.get('Range')
    if (range) {
      //only hit the cache if the range is set otherwise its not worth it for the individual json files etc
      const hash = `${entryId}/${url}/${generation}/${range}`
      const cachedData = await cache.get(hash)
      if (!isNil(cachedData)) {
        return new Response(cachedData, {
          status: 200,
          statusText: 'OK',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Accept-Ranges': 'bytes',
            'Content-Range': range,
          },
        })
      } else {
        //handle the cachemiss case
        const response = await defaultFetch(url, baseUrl, axios, init)
        if (response.ok) {
          const data = await response.arrayBuffer()
          await cache.set(hash, data)
          return new Response(data, {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'application/octet-stream',
              'Accept-Ranges': 'bytes',
              'Content-Range': range,
            },
          })
        }
      }
    }
  }

  // If a range header is not set, we can just use the default fetch
  // to get the data for metadata file
  return defaultFetch(url, baseUrl, axios, init)
}

const DEFAULT_CACHE = new PointCloudCache()

export const useLoadPotrees = ({
  pointCloudOctreesRef,
  pointCloudEntries,
  axios,
  cache = DEFAULT_CACHE,
  pointBudget,
  fileName = 'metadata.json',
  onPointCloudLoad,
  onApplyInitialSettings,
  onFrameBeforeUpdatePointClouds,
}: {
  pointCloudOctreesRef: React.MutableRefObject<PointCloudOctree[]>
  pointCloudEntries: readonly GeckoPotreePointCloud[]
  axios: AuthedAxiosRequests
  cache?: PointCloudCache
  pointBudget: number
  fileName?: string
  onPointCloudLoad: (pco: PointCloudOctree, pc: GeckoPotreePointCloud) => void
  onApplyInitialSettings: (pco: PointCloudOctree) => void
  onFrameBeforeUpdatePointClouds?: () => void
}) => {
  const { scene, camera, gl } = useThree()
  const potreeRef = useRef(new Potree())
  const { ROLODEX_URL } = useRuntimeVariables()
  const [applyInitialSettings, setApplyInitialSettings] = useState(false)

  useEffect(() => {
    const potree = potreeRef.current
    // Use the shared point budget, not individual budget per cloud
    potree.pointBudget = pointBudget

    const loadPromises = pointCloudEntries.map((pc) => {
      const baseUrl = `${ROLODEX_URL}/api/v2/files/${pc.id}`
      return potree
        .loadPointCloud(fileName, (url, init) => {
          return fetchWithCache({
            url,
            baseUrl,
            entryId: pc.id,
            cache,
            axios,
            generation: pc.generation,
            init,
          })
        })
        .then((pco) => {
          scene.add(pco as unknown as Object3D)
          pointCloudOctreesRef.current.push(pco)
          onPointCloudLoad(pco, pc)
          setApplyInitialSettings((prev) => !prev)
          return pco
        })
    })

    Promise.all(loadPromises).catch((err) =>
      console.error('Error loading one or more point clouds:', err)
    )

    return () => {
      pointCloudOctreesRef.current.forEach((pco) => {
        scene.remove(pco as unknown as Object3D)
        pco.dispose()
      })
      pointCloudOctreesRef.current = []
    }
  }, [
    pointCloudEntries,
    axios,
    cache,
    pointBudget,
    fileName,
    ROLODEX_URL,
    scene,
    pointCloudOctreesRef,
    onPointCloudLoad,
  ])

  useEffect(() => {
    if (applyInitialSettings) {
      // Avoid linter error - just need to trigger this on point cloud load
    }
    pointCloudOctreesRef.current.forEach((pco) => {
      onApplyInitialSettings(pco)
    })
  }, [applyInitialSettings, onApplyInitialSettings, pointCloudOctreesRef])

  useFrame(() => {
    onFrameBeforeUpdatePointClouds?.()
    potreeRef.current.updatePointClouds(
      pointCloudOctreesRef.current,
      camera,
      gl
    )
  })

  return { potreeRef }
}
